import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import { getItem } from '../utils/utils';
import { socketUrl } from '../utils/constants';

interface SignalRContextType {
  connection: HubConnection | null;
}

const SignalRContext = createContext<SignalRContextType>({ connection: null });

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const { token } = useAuth();

  useEffect(() => {
    // Get token from auth context or localStorage
    const authToken = token || getItem('token');

    if (!authToken) {
      console.log(
        'No authentication token available, skipping SignalR connection'
      );
      return;
    }

    console.log('Creating SignalR connection to:', socketUrl);
    const newConnection = new HubConnectionBuilder()
      .withUrl(socketUrl, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        accessTokenFactory: () => {
          console.log(
            'Providing token for SignalR connection:',
            authToken ? 'Token present' : 'No token'
          );
          return authToken;
        },
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    setConnection(newConnection);

    const startConnection = async () => {
      try {
        await newConnection.start();
        console.log('SignalR Connected successfully:', socketUrl);
      } catch (error) {
        console.error('SignalR Connection Error:', error);
        setTimeout(startConnection, 2000);
      }
    };

    if (newConnection.state === 'Disconnected') {
      startConnection();
    }

    newConnection.onclose((error) => {
      console.log('SignalR Connection Closed:', error || 'No error details');
      startConnection();
    });

    return () => {
      if (newConnection) {
        newConnection.stop().then(() => console.log('SignalR Disconnected'));
      }
    };
  }, [token]);

  return (
    <SignalRContext.Provider value={{ connection }}>
      {children}
    </SignalRContext.Provider>
  );
};

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context.connection;
};
